/**
 * NexusClinicianInbox - Referrals routed to the current clinician
 * Accept / Decline / Redirect workflow
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Inbox, CheckCircle2, XCircle, ArrowUpRight, Loader2,
  Clock, AlertTriangle, ChevronDown, ChevronUp
} from "lucide-react";

const DECLINE_REASONS = [
  { value: "caseload_full", label: "Caseload at capacity" },
  { value: "service_mismatch", label: "Service type not appropriate" },
  { value: "scheduling_conflict", label: "Scheduling conflict" },
  { value: "geographic_distance", label: "Geographic distance" },
  { value: "clinical_contraindication", label: "Clinical contraindication" },
  { value: "other", label: "Other (specify)" },
];

const NexusClinicianInbox = ({ token, API, user }) => {
  const [data, setData] = useState({ pending: [], responded: [] });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [responding, setResponding] = useState(null);
  const [responseForm, setResponseForm] = useState({ reason_code: "", reason_text: "" });

  const fetchMyReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/nexus/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.data || { pending: [], responded: [] });
    } catch (err) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchMyReferrals(); }, [fetchMyReferrals]);

  const handleRespond = async (referralId, responseType, redirectTo = null) => {
    setResponding(referralId + responseType);
    try {
      const payload = {
        response: responseType,
        reason_code: responseForm.reason_code,
        reason_text: responseForm.reason_text,
      };
      if (redirectTo) payload.redirect_clinician_id = redirectTo;

      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/respond`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Response recorded: ${responseType}`);
        setExpandedId(null);
        setResponseForm({ reason_code: "", reason_text: "" });
        fetchMyReferrals();
      } else {
        toast.error(json.detail || "Response failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setResponding(null);
    }
  };

  const ReferralCard = ({ ref, showActions }) => {
    const isExpanded = expandedId === ref.referral_id;
    const urgencyColors = {
      routine: "bg-slate-50 text-slate-600 border-slate-200",
      urgent: "bg-amber-50 text-amber-700 border-amber-200",
      emergent: "bg-red-50 text-red-700 border-red-200",
    };

    return (
      <Card
        data-testid={`inbox-referral-${ref.referral_id}`}
        className={`border-slate-200 ${ref.urgency === "emergent" ? "border-l-2 border-l-red-500" : ref.urgency === "urgent" ? "border-l-2 border-l-amber-500" : ""}`}
      >
        <CardContent className="py-3 px-4">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setExpandedId(isExpanded ? null : ref.referral_id)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{ref.client_first_name} {ref.client_last_name}</span>
                <Badge variant="outline" className={`text-[10px] border ${urgencyColors[ref.urgency] || ""}`}>
                  {ref.urgency}
                </Badge>
                {ref.risk_level === "high" || ref.risk_level === "critical" ? (
                  <Badge variant="outline" className="text-[10px] border-red-300 text-red-700 bg-red-50">
                    {ref.risk_level === "critical" ? "CRITICAL" : "HIGH RISK"}
                  </Badge>
                ) : null}
                {ref.my_response && (
                  <Badge className={`text-[10px] border-0 ${ref.my_response === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {ref.my_response}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {ref.service_requested || "—"} &middot; {ref.client_county || "—"} &middot; Routed {ref.my_routed_at ? new Date(ref.my_routed_at).toLocaleDateString() : "—"}
              </p>
            </div>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>

          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              {/* Clinical info */}
              <div className="text-sm">
                <p className="text-slate-700">{ref.presenting_need || "No presenting need documented."}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(ref.diagnosis_codes || []).map((dx, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{dx}</Badge>
                  ))}
                </div>
                <div className="flex gap-4 text-xs text-slate-500 mt-2">
                  <span>DOB: {ref.client_dob || "—"}</span>
                  <span>Gender: {ref.client_gender || "—"}</span>
                  <span>Language: {ref.language_preference || "English"}</span>
                  <span>Modality: {ref.preferred_modality || "any"}</span>
                </div>
              </div>

              {/* Action buttons */}
              {showActions && !ref.my_response && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleRespond(ref.referral_id, "accepted")}
                      disabled={!!responding}
                      data-testid={`accept-btn-${ref.referral_id}`}
                    >
                      {responding === ref.referral_id + "accepted" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => handleRespond(ref.referral_id, "declined")}
                      disabled={!!responding}
                      data-testid={`decline-btn-${ref.referral_id}`}
                    >
                      {responding === ref.referral_id + "declined" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                      Decline
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={responseForm.reason_code} onValueChange={(v) => setResponseForm(p => ({ ...p, reason_code: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Reason (optional)" /></SelectTrigger>
                      <SelectContent>
                        {DECLINE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Textarea
                      placeholder="Additional notes..."
                      value={responseForm.reason_text}
                      onChange={(e) => setResponseForm(p => ({ ...p, reason_text: e.target.value }))}
                      rows={1}
                      className="text-xs h-8 min-h-[2rem]"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div data-testid="nexus-clinician-inbox" className="space-y-4">
      {/* Pending */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Inbox className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-slate-900">Pending Response ({data.pending?.length || 0})</h3>
        </div>
        {data.pending?.length > 0 ? (
          <div className="space-y-2">
            {data.pending.map(ref => <ReferralCard key={ref.referral_id} ref_={ref} ref={ref} showActions={true} />)}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-6 text-center text-slate-500 text-sm">
              No pending referrals
            </CardContent>
          </Card>
        )}
      </div>

      {/* Responded */}
      {data.responded?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-900">Responded ({data.responded?.length || 0})</h3>
          </div>
          <div className="space-y-2">
            {data.responded.map(ref => <ReferralCard key={ref.referral_id} ref_={ref} ref={ref} showActions={false} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default NexusClinicianInbox;
